import React, { useState, useEffect } from 'react';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  registerPasskey,
  authenticateWithPasskey,
} from '../utils/passkeys';
import './LoginPage.css';
import { apiClient, sessionManager } from '../api';
import { ROUTES } from '../routes';

interface LoginPageProps {
  onLogin: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [supported, setSupported] = useState(false);
  const [platformAuthAvailable, setPlatformAuthAvailable] = useState(false);
  const [authMode, setAuthMode] = useState<'passkey' | 'saml' | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    // Check if already logged in
    const checkAuthAndMode = async () => {
      if (sessionManager.getSessionToken()) {
        try {
          console.log('[LoginPage] Checking auth with token');
          const authCheck = await apiClient.fetch('/api/auth/check');
          console.log('[LoginPage] Auth check response status:', authCheck.status);
          if (authCheck.ok) {
            const data = await authCheck.json();
            console.log('[LoginPage] Auth check response data:', data);
            // Already authenticated, redirect to dashboard
            console.log('[LoginPage] Already authenticated - redirecting to dashboard');
            window.location.href = ROUTES.DASHBOARD;
            return;
          } else {
            console.log('[LoginPage] Auth check failed - continuing to login');
          }
        } catch (err) {
          // Not authenticated, continue to login
          console.log('[LoginPage] Auth check error:', err);
        }
      }

      // Fetch authentication mode from server
      try {
        const response = await apiClient.fetch('/api/v1/auth/mode');
        const data = await response.json();
        setAuthMode(data.mode);

        // Only check WebAuthn support if using passkey mode
        if (data.mode === 'passkey') {
          setSupported(isWebAuthnSupported());
          isPlatformAuthenticatorAvailable().then(setPlatformAuthAvailable);
        }
      } catch (err) {
        console.error('Failed to fetch auth mode:', err);
        // Default to passkey mode
        setAuthMode('passkey');
        setSupported(isWebAuthnSupported());
        isPlatformAuthenticatorAvailable().then(setPlatformAuthAvailable);
      } finally {
        setInitializing(false);
      }
    };

    checkAuthAndMode();

    // Load last successful username from cookie
    const savedUsername = sessionManager.getCookie('lastUsername');
    if (savedUsername) {
      setUsername(savedUsername);
    }
  }, []);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await registerPasskey(username);
      setSuccess('Passkey registered successfully! You can now log in.');
      setMode('login');
      setUsername('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    // Handle SAML redirect separately to avoid resetting loading state
    if (authMode === 'saml') {
      // Small delay to allow UI to update before redirect
      await new Promise(resolve => setTimeout(resolve, 100));
      // Redirect to SAML login endpoint
      window.location.href = '/api/saml/login';
      // Don't reset loading - page is navigating away
      return;
    }

    try {
      await authenticateWithPasskey(username);
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      setLoading(false);
    }
  };

  // Loading state while fetching auth mode
  if (authMode === null || initializing) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img
              src="/dashboard/aff_logo.svg"
              alt="Affinidi Logo"
              style={{ maxWidth: '200px', marginBottom: '0px' }}
            />
            <h1>
              Affinidi Trust Fabric
              <br />
              Trust Gateway
            </h1>
          </div>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        </div>
      </div>
    );
  }

  // SAML mode - show simple login button
  if (authMode === 'saml') {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img
              src="/dashboard/aff_logo.svg"
              alt="Affinidi Logo"
              style={{ maxWidth: '200px', marginBottom: '0px' }}
            />
            <h1>
              Affinidi Trust Fabric
              <br />
              Trust Gateway
            </h1>
            <h2>Sign In</h2>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleLogin}>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Redirecting...
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt"></i>
                  Sign in with SSO
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Check if WebAuthn is not supported (passkey mode only)
  if (!supported && authMode === 'passkey') {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2>WebAuthn Not Supported</h2>
          <p className="error-message">
            Your browser doesn't support WebAuthn/Passkeys. Please use a modern browser like Chrome,
            Edge, Safari, or Firefox.
          </p>
        </div>
      </div>
    );
  }

  // Only check WebAuthn support for passkey mode
  if (!supported && authMode === 'passkey') {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2>WebAuthn Not Supported</h2>
          <p className="error-message">
            Your browser doesn't support WebAuthn/Passkeys. Please use a modern browser like Chrome,
            Edge, Safari, or Firefox.
          </p>
        </div>
      </div>
    );
  }

  // Passkey mode - show original passkey UI
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img
            src="/dashboard/aff_logo.svg"
            alt="Affinidi Logo"
            style={{ maxWidth: '200px', marginBottom: '0px' }}
          />
          <h1>
            Affinidi Trust Fabric
            <br />
            Trust Gateway
          </h1>
          <h2>{mode === 'login' ? 'Sign In' : 'Register'}</h2>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="Enter your username"
              required
              disabled={loading}
              autoComplete="username"
              autoFocus
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading || !username.trim()}>
            {loading ? (
              <>
                <span className="spinner"></span>
                {mode === 'login' ? 'Authenticating...' : 'Registering...'}
              </>
            ) : (
              <>
                <i className={`fas ${mode === 'login' ? 'fa-fingerprint' : 'fa-user-plus'}`}></i>
                {mode === 'login' ? 'Sign in with Passkey' : 'Register Passkey'}
              </>
            )}
          </button>
        </form>

        <div className="login-footer">
          {mode === 'login' ? (
            <p>
              Don't have a passkey?{' '}
              <button
                className="link-button"
                onClick={() => {
                  setMode('register');
                  setError(null);
                  setSuccess(null);
                }}
                disabled={loading}
              >
                Register here
              </button>
            </p>
          ) : (
            <p>
              Already have a passkey?{' '}
              <button
                className="link-button"
                onClick={() => {
                  setMode('login');
                  setError(null);
                  setSuccess(null);
                }}
                disabled={loading}
              >
                Sign in here
              </button>
            </p>
          )}
        </div>

        {platformAuthAvailable && (
          <div className="platform-auth-note">
            <i className="fas fa-shield-alt"></i>
            Biometric authentication available on this device
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
